/**
 * <TutorAbsenceCreditsNotice> — les crédits d'absence du masteur, sous la séance qu'il s'apprête à
 * déclarer (molecule).
 *
 * L'encadré « Important » / « Attention » de l'ancien `ManageRDVScreen`, ou sa ligne discrète quand
 * le bloc d'impact est déjà là (`tutorAbsenceCreditsMessage`), et la note du mois prochain. La
 * charte d'engagement, lien dans le texte de l'ancien écran, devient un lien sous le texte.
 */
import React from "react"

import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { AlertCard } from "../AlertCard"
import { type UITutorAbsenceCredits } from "../../../ports/primary/ui/types/tutorLives"
import { tutorAbsenceCreditsMessage } from "./tutorAbsenceCreditsMessage"

const CHARTER = "Lire la charte d'engagement"

export type TutorAbsenceCreditsNoticeProps = {
    credits: UITutorAbsenceCredits
    /** Un élève change de masteur : le bloc d'impact est au-dessus, le décompte se fait discret. */
    tutorChange?: boolean
    /** Tous changent de masteur, la séance se vide : aucun crédit n'est décompté. */
    emptiesSession?: boolean
    onOpenCharter?: () => void
    testID?: string
}

export function TutorAbsenceCreditsNotice(props: TutorAbsenceCreditsNoticeProps) {
    const { credits, tutorChange = false, emptiesSession = false, onOpenCharter, testID } = props
    const { main, nextMonth } = tutorAbsenceCreditsMessage(credits, { tutorChange, emptiesSession })
    const charter =
        onOpenCharter != null ? (
            <Row>
                <TextButton label={CHARTER} onPress={onOpenCharter} size="sm" />
            </Row>
        ) : null

    if (main.kind === "line") {
        return (
            <Stack gap="1" testID={testID}>
                <Text size="text-sm" color={main.tone === "danger" ? "text-danger" : "text-muted"}>
                    {main.text}
                </Text>
                {nextMonth != null && (
                    <Text size="text-sm" color="text-muted">
                        {nextMonth}
                    </Text>
                )}
                {main.charter && charter}
            </Stack>
        )
    }

    return (
        <AlertCard
            testID={testID}
            severity={main.severity}
            title={main.title}
            message={
                <Stack gap="2">
                    {[...main.lines, ...(nextMonth != null ? [nextMonth] : [])].map((line) => (
                        <Text key={line} size="text-sm" color="text-primary">
                            {line}
                        </Text>
                    ))}
                </Stack>
            }
            actions={charter}
        />
    )
}
